
import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserData {
  id: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
}

interface DeleteUserDialogProps {
  user: UserData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserDeleted?: () => void;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  user,
  open,
  onOpenChange,
  onUserDeleted
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!user) return;
    
    setIsDeleting(true);
    
    try {
      // Determine the correct user ID to use - this is critical for the delete operation
      // If user data comes from user_profiles, it will have user_id, otherwise use id
      const userId = user.user_id || user.id;
      
      if (!userId) {
        throw new Error("User ID not found");
      }
      
      console.log(`Attempting to delete user with ID: ${userId}`);

      // Get the current user's admin status
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }
      
      // Check admin status from database
      const { data: hasAdminRole, error: roleError } = await supabase.rpc('has_role', { 
        role_param: 'admin' 
      });
      
      if (roleError) {
        console.error("Error checking admin role:", roleError);
        throw new Error("Failed to verify admin status");
      }
      
      if (!hasAdminRole && localStorage.getItem('isAdmin') !== 'true') {
        throw new Error("You don't have permission to delete users");
      }
      
      // Call the delete_user RPC function with a UUID parameter
      const { error: deleteError } = await supabase.rpc('delete_user', {
        user_id_param: userId
      });
      
      if (deleteError) {
        console.error("Error deleting user:", deleteError);
        throw new Error(`Failed to delete user: ${deleteError.message}`);
      }
      
      console.log("User successfully deleted");
      toast.success(`User ${user.first_name || ''} ${user.last_name || ''} has been deleted.`);
      
      onOpenChange(false);
      if (onUserDeleted) {
        onUserDeleted();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(`Failed to delete user: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {user?.first_name} {user?.last_name}? This action cannot be undone and will remove all user data including profiles, journal entries, and therapist relationships.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirmDelete();
            }}
            disabled={isDeleting}
            className="bg-red-500 hover:bg-red-600"
          >
            {isDeleting ? "Deleting..." : "Delete User"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteUserDialog;
